import { useCallback, useEffect, useState } from "react";
import Errors, { getFieldErrors } from "../../models/errors";
import { createItem, getItem } from "../../api/generics";
import { NOT_FOUND, SUCCESS } from "../../utils/constants/tags";
import { errorAlert, warningAlert } from "../utils/messages";
import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
} from "@coreui/react";
import { FieldErrors } from "../form/FieldErrors";
import { emptyValueOnUndefined } from "../../utils/fields";
import { Spinner } from "react-bootstrap";
import Loading from "../indicators/Loading";
import Company from "../../models/company";
import CompanyLimits from "../../models/company-limits";

interface CompanyLimitsFormProps {
  company?: Company;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
  clearOnSuccess?: boolean;
}

const CompanyLimitsForm: React.FC<CompanyLimitsFormProps> = ({
  company,
  onCancel,
  onSuccess,
  clearOnSuccess,
}) => {
  // TODO: verify error with regular clearOnSuccess bool
  // eslint-disable-next-line
  const [shouldClearOnSuccess, _] = useState(clearOnSuccess);

  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [limits, setLimits] = useState<CompanyLimits>({});

  const onMaxAmountBranchOfficesChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setLimits((prev) => {
      let newLimit: number | undefined = undefined;
      if (!isNaN(parseInt(e.target.value))) {
        newLimit = parseInt(e.target.value);
      }
      return { ...prev, maxAssets: newLimit };
    });
  };

  const onSave = useCallback(async () => {
    if (company === undefined) {
      return;
    }
    const newLimits = { ...limits, companyId: company.id };

    const requestStatus = await createItem(
      `/admin/companies/${company.id}/limits/`,
      newLimits
    );

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.errors !== undefined) {
        setErrors(requestStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (requestStatus.detail !== undefined) {
        message = requestStatus.detail;
      }
      errorAlert(message);
      setSubmitting(false);
    } else {
      if (shouldClearOnSuccess) {
        setErrors({});
        clearForm();
      }

      onSuccess();
    }

    if (shouldClearOnSuccess) {
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, limits]);

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    setLimits({});
  };

  const loadLimits = async (newCompany?: Company) => {
    setLoading(true);
    if (newCompany === undefined || newCompany.id === undefined) {
      setLimits({});
      setLoading(false);
      return;
    }

    const requestStatus = await getItem<CompanyLimits>(
      `/admin/companies/${newCompany.id}/limits/`
    );

    if (
      requestStatus.status !== SUCCESS &&
      requestStatus.status !== NOT_FOUND
    ) {
      let message = "Ha ocurrido un error";
      if (requestStatus.detail !== undefined) {
        message = requestStatus.detail;
      }

      warningAlert(message);

      setLimits({});
      setLoading(false);
      return;
    }

    if (
      requestStatus.status === NOT_FOUND ||
      requestStatus.data === undefined
    ) {
      setLimits({});
      setLoading(false);
      return;
    }

    setLimits(requestStatus.data);
    setLoading(false);
  };

  useEffect(() => {
    loadLimits(company);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  if (loading) {
    return <Loading height="30vh" />;
  }

  return (
    <>
      <fieldset disabled={submitting}>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Límite cant. activos:</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                onChange={onMaxAmountBranchOfficesChange}
                placeholder="Límite cant. activos:"
                value={emptyValueOnUndefined(limits.maxAssets)}
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("maxAssets", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onClose}>
              Salir
            </CButton>
            <CButton type="submit" color="primary" onClick={onSave}>
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default CompanyLimitsForm;
